import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FlaskConical, Beaker, Factory, Microscope, Package, ArrowRight,
  CheckCircle2, Search, FileText, Truck,
} from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";

const ICONS = [Search, FileText, CheckCircle2, Truck];
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("tr-TR"));

export default function HomePage() {
  const [cats, setCats] = useState([]);
  const [secs, setSecs] = useState([]);
  const [stats, setStats] = useState(null);
  const sc = useSiteContent();
  const h = sc?.hero || {};
  const STEPS = (sc?.adimlar || []).map((s, i) => ({ ...s, icon: ICONS[i % 4] }));
  const featured = sc?.one_cikan_kategoriler || [];

  useEffect(() => {
    api.get("/site-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/catalog/category-tree").then((r) => setCats(r.data.items || [])).catch(() => {});
    api.get("/catalog/sectors").then((r) => setSecs(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="hero-grid text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide text-teal-200 mb-5">
              {h.rozet}
            </div>
            <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" data-testid="hero-title">
              {h.baslik1}<br />
              <span className="text-teal-400">{h.baslik2}</span>
            </h1>
            <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed" data-testid="hero-desc">{h.aciklama}</p>
            <div className="mt-8 max-w-2xl">
              <SearchBar large />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/urunler"><Button className="bg-teal-600 hover:bg-teal-700 h-11" data-testid="hero-catalog-btn">
                Ürün Kataloğu <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
              <Link to="/tedarik-sureci"><Button variant="outline" className="h-11 bg-transparent border-white/30 text-white hover:bg-white/10">
                Tedarik Süreci</Button></Link>
            </div>
            <p className="mt-5 text-sm text-slate-400 flex items-center gap-2" data-testid="hero-trust-line">
              <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0" />
              {h.guven_satiri}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 grid grid-cols-2 gap-4">
            {h.gorsel && <img src={h.gorsel} alt="" className="col-span-2 rounded-xl border border-white/10 max-h-56 w-full object-cover" data-testid="hero-image" />}
            {[[fmt(stats?.urun), "Yayındaki Ürün"], [fmt(stats?.sektor), "Sektör"], [fmt(stats?.kullanim_alani), "Kullanım Alanı"], [fmt(stats?.marka), "Marka"]].map(([n, l], i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-testid={`hero-stat-${i}`}>
                <div className="font-head text-2xl font-bold text-teal-300">{n}</div>
                <div className="text-sm text-slate-300 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-head text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">Nasıl Çalışır?</h2>
        <p className="text-slate-500 mb-8">Teklif alma süreci dört adımda ilerler.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 relative fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="w-11 h-11 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="text-xs font-semibold text-teal-600 mb-1">ADIM {i + 1}</div>
              <h3 className="font-head font-semibold text-slate-900">{s.t}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-head text-2xl sm:text-3xl font-semibold text-slate-900 mb-8">Ürün Kategorileri</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(featured.length ? featured.map((id) => cats.find((c) => c.kategori_id === id)).filter(Boolean) : cats.filter((c) => !c.ust_kategori_id)).map((c, i) => (
              <Link key={c.kategori_id} to={c.seo_slug ? `/kategori/${c.seo_slug}` : `/urunler?kategori=${c.kategori_id}`} data-testid={`home-cat-${i}`}
                className="group border border-slate-200 rounded-xl p-5 hover:border-teal-400 hover:shadow-md transition-all flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  {[Beaker, Factory, Microscope, Package, FlaskConical][i % 5] &&
                    React.createElement([Beaker, Factory, Microscope, Package, FlaskConical][i % 5], { className: "w-5 h-5" })}
                </div>
                <div>
                  <div className="font-medium text-slate-900 group-hover:text-teal-700">{c.kategori_adi}</div>
                  <div className="text-xs text-slate-400">{c.toplam_urun_sayisi} ürün</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-head text-2xl sm:text-3xl font-semibold text-slate-900">Sektöre Göre Keşfedin</h2>
          <Link to="/sektorler" className="text-sm text-teal-600 hover:underline hidden sm:block">Tümünü gör →</Link>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {secs.slice(0, 16).map((s) => (
            <Link key={s.sektor_id} to={`/urunler?sektor=${s.sektor_id}`} data-testid={`home-sector-${s.sektor_id}`}
              className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:border-teal-400 hover:text-teal-700 transition-colors">
              {s.sektor_adi_tr}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
